// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatToMoney } from '@shared/ui-kit/utils';
import { useTranslation } from 'react-i18next';
import { useAggregationData } from '@shared/advisory/aggregation/providers/aggregation-provider';
import { useMemo, useState } from 'react';
import { type KpiPeriod, type KpiBoxesProps } from '@shared/ui-kit/components';
import { produce } from 'immer';
import { type LoaderStatus } from '../loader';
import { type SeriesDataKeys } from '../historical-balances-chart/use-historical-balances-chart';
import { useNavigate } from 'react-router';

export const useSummary = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [activeSerieId, setActiveSerieId] = useState<SeriesDataKeys>('net_value');

  const {
    institutionsHistoricalBalances,
    historicalBalances,
    hasHistoricalBalances,
    periodPicker,
    currency,
    historicalBalancesStatus,
    illiquidAssetsStatus,
    updatedDate,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    onRefresh,
    institutionId,
    institutions,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    accountsPositions,
    illiquidAssetsByTag,
    shouldLoadIlliquidAssets,
    refreshMode,
  } = useAggregationData();

  const status = useMemo((): LoaderStatus => {
    const statuses = shouldLoadIlliquidAssets
      ? [historicalBalancesStatus, illiquidAssetsStatus]
      : [historicalBalancesStatus];

    const isLoading = statuses.includes('pending');

    const isError = statuses.includes('rejected');

    const isFetching = statuses.includes('fetching');

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [historicalBalancesStatus, illiquidAssetsStatus, shouldLoadIlliquidAssets]);

  const institutionsBreakdown = useMemo(() => {
    const activeSeriesKeyBySerie: Record<
      SeriesDataKeys,
      'net_value' | 'investment' | 'cash' | 'debt'
    > = {
      net_value: 'net_value',
      investment: 'investment',
      cash: 'cash',
      debt: 'debt',
    };
    const activeSeriesKey = activeSeriesKeyBySerie[activeSerieId];

    const totalValue = institutionsHistoricalBalances.reduce(
      (acc, curr) => acc + (curr[activeSeriesKey] ?? 0),
      0,
    );

    const institutionsBreakdownMap = institutionsHistoricalBalances.reduce(
      (acc, balances) => {
        return produce(acc, draft => {
          if (draft.has(balances.institution_id)) {
            draft.get(balances.institution_id)!.value +=
              balances[activeSeriesKey] ?? 0;
          } else {
            draft.set(balances.institution_id, {
              value: balances[activeSeriesKey] ?? 0,
            });
          }
        });
      },
      new Map<string, { value: number }>(),
    );

    const institutionsBreakdown = Array.from(
      institutionsBreakdownMap.entries(),
      ([institutionId, { value }]) => {
        const institution = institutions.find(
          institution => institution.id === institutionId,
        );

        return {
          name: institution?.name ?? 'Others',
          id: institutionId,
          value,
          weight: totalValue > 0 ? value / totalValue : 0,
        };
      },
    );

    return {
      institutions: institutionsBreakdown.map(institution => institution.name),
      weights: institutionsBreakdown.map(institution => institution.weight),
    };
  }, [activeSerieId, institutions, institutionsHistoricalBalances]);

  const kpis = useMemo<KpiBoxesProps[]>(() => {
    const illiquidValuationTotal = shouldLoadIlliquidAssets
      ? illiquidAssetsByTag.reduce(
          (sum, asset) => sum + (asset.current_valuation ?? 0),
          0,
        )
      : 0;

    const accountBalance =
      historicalBalances.cash[historicalBalances.cash.length - 1] ?? 0;

    const initialAccountBalance = historicalBalances.cash[0] ?? 0;

    const finalAccountBalance =
      historicalBalances.cash[historicalBalances.cash.length - 1] ?? 0;

    const accountBalanceReturn =
      initialAccountBalance !== 0
        ? (finalAccountBalance - initialAccountBalance) / initialAccountBalance
        : 0;

    const investmentTotal =
      historicalBalances.investment[historicalBalances.investment.length - 1] ?? 0;

    const initialInvestment = historicalBalances.investment[0] ?? 0;

    const finalInvestment =
      historicalBalances.investment[historicalBalances.investment.length - 1] ?? 0;

    const investmentReturn =
      initialInvestment !== 0
        ? (finalInvestment - initialInvestment) / initialInvestment
        : 0;

    const debtTotal =
      historicalBalances.debt[historicalBalances.debt.length - 1] ?? 0;

    const initialDebt = historicalBalances.debt[0] ?? 0;

    const finalDebt =
      historicalBalances.debt[historicalBalances.debt.length - 1] ?? 0;

    const debtReturn =
      initialDebt !== 0 ? (finalDebt - initialDebt) / initialDebt : 0;

    const netWorthTotal =
      accountBalance + investmentTotal + illiquidValuationTotal - debtTotal;

    const period = periodPicker.defaultHist as KpiPeriod;

    return [
      {
        text: t('aggregator.summary-net-worth', { ns: 'aggregator' }),
        value: formatToMoney(netWorthTotal, currency),
        dataTestid: 'kpi-summary-net-worth',
        icon: 'ChartColumn',
        color: 'main',
      },
      {
        text: t('aggregator.summary-account-balance', { ns: 'aggregator' }),
        value: formatToMoney(accountBalance, currency),
        dataTestid: 'kpi-summary-account-balance',
        onClick: () => navigate('/aggregation/cashflow'),
        icon: 'Wallet',
        color: 'success',
        period,
        currency,
        return: accountBalanceReturn,
        returnInvestment: accountBalance - initialAccountBalance,
      },
      {
        text: t('common.investment', { ns: 'common' }),
        value: formatToMoney(investmentTotal, currency),
        dataTestid: 'kpi-summary-investment',
        onClick: () => navigate('/aggregation/investment'),
        icon: 'TrendingUp',
        color: 'success',
        period,
        currency,
        return: investmentReturn,
        returnInvestment: investmentTotal - initialInvestment,
      },
      ...(shouldLoadIlliquidAssets
        ? [
            {
              text: t('aggregator.illiquid-assets', { ns: 'aggregator' }),
              value: formatToMoney(illiquidValuationTotal, currency),
              dataTestid: 'kpi-summary-illiquid-assets',
              onClick: () => navigate('/aggregation/illiquids'),
              icon: 'Building',
              color: 'secondary',
            } as KpiBoxesProps,
          ]
        : []),
      {
        text: t('glossary.debt', { ns: 'glossary' }),
        value: formatToMoney(debtTotal, currency),
        dataTestid: 'kpi-summary-debt',
        onClick: () => navigate('/aggregation/loans'),
        icon: 'CreditCard',
        color: 'danger',
        period,
        currency,
        return: debtReturn,
        returnInvestment: debtTotal - initialDebt,
        lowerIsBetter: true,
      },
    ];
  }, [
    shouldLoadIlliquidAssets,
    illiquidAssetsByTag,
    historicalBalances.cash,
    historicalBalances.investment,
    historicalBalances.debt,
    t,
    currency,
    periodPicker.defaultHist,
    navigate,
  ]);

  return {
    t,
    kpis,
    historicalBalances,
    hasHistoricalBalances,
    periodPicker,
    currency,
    status,
    updatedDate,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    onRefresh,
    institutionsBreakdown,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    activeSerieId,
    setActiveSerieId,
    holdings: accountsPositions.holdings,
    refreshMode,
  };
};
